import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Logo from '../../../assets/images/Logo.svg';
import LogoSmall from '../../../assets/images/PawLogo.png';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';

import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HelpIcon from '@mui/icons-material/Help';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MessageIcon from '@mui/icons-material/Message';
import PaidIcon from '@mui/icons-material/Paid';
import PersonIcon from '@mui/icons-material/Person';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuth } from 'context/AuthContext';
import { useCurrentBaseUser } from '../../../hooks/user/useCurrentBaseUser';

const SitterRoutePaths = {
  DASHBOARD: '/sitter-dashboard',
  BOOKINGS: '/sitter-bookings',
  CALENDAR: '/sitter-calendar',
  MESSAGES: '/sitter-messages',
  EARNINGS: '/sitter-earnings',
  PROFILE_PREVIEW: '/sitter-profile-preview',
  //TODO: Remove when user already was onboarded
  ONBOARDING: '/boarding-sitters',
  SETTINGS: '/sitter-settings',
  HELP_CENTER: '/sitter-help-center',
  LOGOUT: '/logout',
};

const menuItemSx = {
  py: 1.5,
  px: 2,
  '&:hover': { backgroundColor: 'rgba(23, 139, 142, 0.08)' },
};

const SitterHeader: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const { data: user } = useCurrentBaseUser();

  const { firstName, photoUrl } = user || {};

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNav = (path: string) => {
    setAnchorEl(null);
    if (path === SitterRoutePaths.LOGOUT) {
      logout();
    } else {
      navigate(path);
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        height: 70,
        backgroundColor: 'white',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, sm: 4 },
        zIndex: 1000,
      }}
    >
      {/* Logo */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => handleNav(SitterRoutePaths.DASHBOARD)}
      >
        <img
          src={isBelowMd ? LogoSmall : Logo}
          alt="HappyPaws Logo"
          style={{ width: isBelowMd ? '40px' : '205px', height: isBelowMd ? '44px' : '45px' }}
        />
      </Box>

      {/* Hamburger + Avatar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        onClick={handleMenuClick}
        sx={{
          minWidth: 120,
          border: '1px solid gray',
          p: 0.5,
          gap: 2,
          borderRadius: 4,
          cursor: 'pointer',
          backgroundColor: open ? 'rgba(23, 139, 142, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(64, 78, 78, 0.08)',
          },
        }}
      >
        <MenuIcon />
        <Avatar
          src={photoUrl || undefined}
          alt={firstName}
          sx={{ width: 36, height: 36, backgroundColor: '#178B8E', fontSize: '0.9rem' }}
        >
          {photoUrl ? '' : 'U'}
        </Avatar>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 1,
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 220,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem onClick={() => handleNav(SitterRoutePaths.DASHBOARD)} sx={menuItemSx}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dashboard</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.BOOKINGS)} sx={menuItemSx}>
          <ListItemIcon>
            <LibraryBooksIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bookings</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.CALENDAR)} sx={menuItemSx}>
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Calendar</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.MESSAGES)} sx={menuItemSx}>
          <ListItemIcon>
            <MessageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Messages</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.EARNINGS)} sx={menuItemSx}>
          <ListItemIcon>
            <PaidIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Earnings</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleNav(SitterRoutePaths.PROFILE_PREVIEW)} sx={menuItemSx}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View My Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.ONBOARDING)} sx={menuItemSx}>
          <ListItemIcon>
            <AssignmentIndIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Sitter Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.SETTINGS)} sx={menuItemSx}>
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleNav(SitterRoutePaths.HELP_CENTER)} sx={menuItemSx}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Help Center</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleNav(SitterRoutePaths.LOGOUT)} sx={menuItemSx}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SitterHeader;
